import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { RefrescoSesionService } from './refresco-sesion.service';

/**
 * Ante un 401, intenta renovar la sesion y reintenta la peticion UNA vez. Si la
 * renovacion tambien falla, cierra sesion y vuelve al formulario.
 *
 * El refresco NO se llama a intervalo: lo dispara el 401. La peticion que se
 * reintenta ya paso por `credentialsInterceptor`, asi que conserva
 * `withCredentials` y el navegador adjunta la cookie nueva.
 */

/**
 * Endpoints de sesion, EXENTOS.
 *
 * ⚠️ Sin esto pasan dos cosas, y ninguna da un error que se entienda:
 *
 *   · El 401 de `refrescar` dispararia otro refresco, y ese otro, y otro: un
 *     bucle que solo para cuando el navegador se rinde.
 *   · El 401 legitimo de `autenticar` —contraseña incorrecta— acabaria
 *     expulsando al usuario del propio login, y el error que ve **no es el
 *     suyo**: en vez de "contraseña incorrecta" lee "tu sesion expiro".
 *
 *   · ⚠️ Y el 401 de `actual` —el que faltaba— es **la respuesta a la pregunta**,
 *     no un fallo. `restaurarSesion()` pregunta «¿hay sesion?» en cada arranque y
 *     "no" es una respuesta perfectamente normal: es lo que contesta la PRIMERA
 *     vez que alguien abre el login. Sin la exencion, ese "no" disparaba un
 *     `POST /sesion/refrescar` que tambien daba 401, y la consola del login
 *     limpio saludaba con DOS errores rojos.
 *
 *     Peor que el ruido: despues del refresco fallido el interceptor hacia
 *     `cerrarSesion()` y navegaba a `/login` —donde ya estabamos—, asi que
 *     «nunca has entrado» y «tu sesion expiro» acababan en el mismo camino. Son
 *     cosas distintas y tienen que distinguirse.
 */
const EXENTOS = [
  '/sesion/actual',
  '/sesion/autenticar',
  '/sesion/refrescar',
  '/sesion/cerrar',
];

export const sesionExpiradaInterceptor: HttpInterceptorFn = (req, next) => {
  // Con la autenticacion simulada no hay backend de sesion al que preguntar: los
  // 401 que aparezcan son de otros servicios y deben llegar al componente.
  if (environment.authSimulado) return next(req);

  const refresco = inject(RefrescoSesionService);
  const auth = inject(AuthService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: unknown) => {
      const es401 = error instanceof HttpErrorResponse && error.status === 401;
      if (!es401 || EXENTOS.some((ruta) => req.url.includes(ruta))) {
        return throwError(() => error);
      }

      return refresco.refrescar().pipe(
        switchMap(() => next(req)),
        catchError(() =>
          auth.cerrarSesion().pipe(
            switchMap(() => {
              void router.navigate(['/login']);
              return throwError(() => error);
            }),
          ),
        ),
      );
    }),
  );
};
